/**
 * AddressAutocompleteUI Theme
 *
 * Theme configuration for the AddressAutocompleteUI component.
 * This component displays address autocomplete predictions as a floating dropdown.
 *
 * Parts:
 * - container: The outer dropdown container (positioned absolutely)
 * - list: The VStack wrapper for prediction items
 * - item: Individual prediction item (clickable)
 * - mainText: Primary text (street/place name)
 * - secondaryText: Secondary text (city, state, country)
 */

export default {
  parts: ['container', 'list', 'item', 'mainText', 'secondaryText'],

  baseStyle: {
    container: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      bg: 'white',
      border: '1px solid',
      borderColor: 'gray.200',
      borderRadius: 'md',
      maxH: '300px',
      overflowY: 'auto',
      shadow: 'lg',
      mt: 1,
    },

    list: {
      align: 'stretch',
      spacing: 0,
      width: '100%',
    },

    item: {
      borderBottom: '1px solid',
      borderColor: 'gray.100',
      cursor: 'pointer',
      p: 3,
      transition: 'background 0.2s',
      width: '100%',

      _hover: {
        bg: 'gray.100',
      },

      _last: {
        borderBottom: 'none',
      },
    },

    mainText: {
      fontSize: 'sm',
      fontWeight: 'medium',
      color: 'gray.900',
    },

    secondaryText: {
      fontSize: 'xs',
      color: 'gray.600',
      mt: 0.5,
    },
  },

  variants: {
    /**
     * Default variant - standard styling
     */
    default: {},

    /**
     * Compact variant - smaller padding and text
     */
    compact: {
      container: {
        maxH: '250px',
      },
      item: {
        p: 2,
      },
      mainText: {
        fontSize: 'xs',
      },
      secondaryText: {
        fontSize: '2xs',
      },
    },

    /**
     * Elevated variant - more prominent shadow
     */
    elevated: {
      container: {
        shadow: 'xl',
        borderColor: 'gray.300',
      },
    },

    /**
     * Bordered variant - thicker border, no shadow
     */
    bordered: {
      container: {
        border: '2px solid',
        borderColor: 'gray.300',
        shadow: 'none',
        borderRadius: 'lg',
      },
    },
  },

  sizes: {
    sm: {
      container: {
        maxH: '200px',
      },
      item: {
        p: 2,
      },
      mainText: {
        fontSize: 'xs',
      },
      secondaryText: {
        fontSize: '2xs',
      },
    },

    md: {
      container: {
        maxH: '300px',
      },
      item: {
        p: 3,
      },
      mainText: {
        fontSize: 'sm',
      },
      secondaryText: {
        fontSize: 'xs',
      },
    },

    lg: {
      container: {
        maxH: '400px',
      },
      item: {
        p: 4,
      },
      mainText: {
        fontSize: 'md',
      },
      secondaryText: {
        fontSize: 'sm',
      },
    },
  },

  defaultProps: {
    variant: 'default',
    size: 'md',
  },
};
